import cloneDeep from 'lodash/cloneDeep';
import {sendForm, sendTemporaryForm, validateTemporaryForm} from '../DNFORM-utils/DNFORM-api';
import {convertArrayToObject} from '../DNFORM-utils/DNFORM-utils';
import {SESSION_META} from '../DNFORM-utils/DNFORM-constants';

export const formSubmitMixin = {
	data() {
		return {
			isLoading: false,
			isSubmitSuccessful: false,
			isSubmitFailed: false,
			recaptchaToken: '',
			metaData: {}
		};
	},
	methods: {
		scrollToTopOfForm() {
			this.$nextTick(() => {
				const form = this.$refs[this.formId];

				if (form) {
					form.scrollIntoView({behavior: 'smooth'});
				}
			});
		},
		createFormPayload() {
			const payload = cloneDeep(this.model);
			// It creates an object with the names of the uploaded files for each file input
			for (const [step, fileInputs] of Object.entries(this.uploadedFiles)) {
				for (const [inputId, files] of Object.entries(fileInputs)) {
					const fileInputObject = {};

					files.forEach((file, index) => {
						fileInputObject[index + 1] = file;
					});

					payload[step][inputId] = fileInputObject;
				}
			}
			// Convert array to objects
			for (const [step, inputs] of Object.entries(payload)) {
				for (const [inputId, inputValue] of Object.entries(inputs)) {
					if (Array.isArray(inputValue)) {
						payload[step][inputId] = convertArrayToObject(inputValue);
					}
				}
			}
			// If it exists and we're completing the form add reCaptcha token to the last step's payload
			if (this.recaptchaToken && this.isLastStep) {
				const lastStepIndex = this.isMultiStepForm ? this.totalSteps - 1 : this.totalSteps;
				payload[lastStepIndex].recaptcha = this.recaptchaToken;
			}

			if (!this.isLastStep) {
				payload[SESSION_META.OBJECT] = this.createMetaDataObject();
			}

			return payload;
		},
		createMetaDataObject() {
			const metaDataLastStep = this.metaData[SESSION_META.LAST_STEP];
			const lastStep = metaDataLastStep && metaDataLastStep > this.currentStep + 1 ? metaDataLastStep : this.currentStep + 1;

			return {
				[SESSION_META.LAST_STEP]: lastStep
			};
		},
		resetSubmitFailed() {
			if (this.isSubmitFailed) {
				this.isSubmitFailed = false;
			}
		},
		getRecaptchaToken() {
			return new Promise((resolve, reject) => {
				this.$recaptchaLoaded()
					.then(() => {
						const recaptchaId = this.$recaptchaInstance.recaptchaID;
						this.$recaptchaInstance.recaptcha.execute(recaptchaId)
							.then(token => {
								resolve(token);
							}).catch(error => {
								reject(error);
							});
					});
			});
		},
		async onSubmit() {
			// Skip form validation if we're on the summary page
			if (!this.isSummary) {
				this.validateForm();
			}

			const isFormValid = Object.values(this.errors.items).every((error) => error === '');

			if (!isFormValid) {
				this.$nextTick(() => {
					this.scrollToFirstInvalidInput();
				});
			} else if (this.formId && this.sessionToken) {
				if (this.isRecaptchaEnabled && this.isLastStep) {
					try {
						this.recaptchaToken = await this.getRecaptchaToken();
					} catch (error) {
						console.error('Failed to get reCaptcha token', error);
					}
				}

				if (this.isLastStep) {
					this.sendFormData();
				} else {
					this.sendTemporaryFormData();
				}
			}
		},
		sendFormData() {
			this.isLoading = true;
			const payload = this.createFormPayload();

			sendForm(this.formId, this.sessionToken, payload)
				.then((response) => this.handleSendFormResponse(response))
				.catch((error) => {
					this.isSubmitFailed = true;
					console.error('Failed send form data', error, 'Params:', {formId: this.formId, sessionToken: this.sessionToken, payload});
				})
				.finally(() => {
					this.scrollToTopOfForm();
					this.isLoading = false;
				});
		},
		validateTemporaryFormData(payload) {
			return new Promise((resolve, reject) => {
				validateTemporaryForm(this.formId, this.sessionToken, this.currentStep, payload)
					.then((response) => {
						this.resetSubmitFailed();
						const isValid = !(response.data[this.currentStep] && response.data[this.currentStep].length);

						if (!isValid) {
							this.handleErrorResponse(response.data);
						}

						resolve(isValid);
					})
					.catch((error) => {
						reject(error);
					});
			});
		},
		sendTemporaryFormData() {
			this.isLoading = true;
			const payload = this.createFormPayload();

			this.validateTemporaryFormData(payload)
				.then((isValid) => {
					if (isValid) {
						sendTemporaryForm(this.formId, this.sessionToken, this.currentStep, payload)
							.then((response) => this.handleSendTemporaryFormResponse(response))
							.catch((error) => {
								this.isSubmitFailed = true;
								console.error('Failed send temporary form data', error,
									'Params:', {formId: this.formId, sessionToken: this.sessionToken, pageId: this.currentStep, payload});
							})
							.finally(() => {
								this.isLoading = false;
							});
					} else {
						this.isLoading = false;
					}
				})
				.catch((error) => {
					this.isLoading = false;
					this.isSubmitFailed = true;
					console.error('Failed validate temporary form data', error,
						'Params:', {formId: this.formId, sessionToken: this.sessionToken, pageId: this.currentStep, payload});
				})
				.finally(() => {
					this.scrollToTopOfForm();
				});
		}
	}
};



// WEBPACK FOOTER //
// ../source/modules/DNFORM/DNFORM-mixins/DNFORM-form-submit-mixin.js